import { Checkbox, Input } from "@mantine/core"
import { Popover } from "@shopify/polaris"
import React, { useContext, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { getLanguageName, LANGUAGE_CODES } from "../../utils/languages.js"
import { AIStateContext } from "../providers/AIStateProvider.jsx"
import { GlobalStateContext } from "../providers/GlobalStateProvider.jsx"

export const AIBulkOrdersButtons = ({ disabled }) => {
	const { state, dispatch } = useContext(GlobalStateContext)
	const { aiState, setAIState } = useContext(AIStateContext)
	const navigate = useNavigate()
	const { t, i18n } = useTranslation()

	const [subscriptionPopoverActive, setSubscriptionPopoverActive] =
		useState(false)

	const [translatePopoverActive, setTranslatePopoverActive] = useState(false)
	const [selectedLanguage, setSelectedLanguage] = useState(null)

	const [isTranslateTitlesChecked, setIsTranslateTitlesChecked] =
		useState(false)
	const [isTranslateDescriptionsChecked, setIsTranslateDescriptionsChecked] =
		useState(false)
	const [isTranslateVariantsChecked, setIsTranslateVariantsChecked] =
		useState(false)
	const [isRewordTitlesChecked, setIsRewordTitlesChecked] = useState(false)
	const [isRewordDescriptionsChecked, setIsRewordDescriptionsChecked] =
		useState(false)

	const [languageSearchInput, setLanguageSearchInput] = useState("")

	const onCheckboxChanged = (taskName, taskInputType, languageOverride) => {
		const taskAlreadyAdded = aiState.bulkAIOrders.find(
			(order) => order.task === `${taskName}:${taskInputType}`,
		)

		if (
			taskAlreadyAdded &&
			taskName === "translate" &&
			taskAlreadyAdded.targetLang !== selectedLanguage
		) {
			setAIState({
				bulkAIOrders: aiState.bulkAIOrders.map((order) => {
					if (order.task === `translate:${taskInputType}`) {
						return {
							...order,
							targetLang: selectedLanguage,
						}
					} else {
						return order
					}
				}),
			})
		} else if (taskAlreadyAdded) {
			setAIState({
				bulkAIOrders: aiState.bulkAIOrders.filter(
					(order) => order.task !== `${taskName}:${taskInputType}`,
				),
			})
		} else if (!taskAlreadyAdded) {
			setAIState({
				bulkAIOrders: [
					...aiState.bulkAIOrders,
					{
						task: `${taskName}:${taskInputType}`,
						...(taskName === "translate" && selectedLanguage
							? { targetLang: selectedLanguage }
							: {}),
					},
				],
			})
		}
	}

	const toggleTranslatePopover = (e) => {
		e.stopPropagation()
		setTranslatePopoverActive(!translatePopoverActive)
	}

	const changeSelectedLanguage = (lang) => {
		setSelectedLanguage(lang)
		setTranslatePopoverActive(false)

		const tasksAlreadyAdded = aiState.bulkAIOrders.find((order) =>
			order.task.includes("translate"),
		)
		const newTasks = []

		if (tasksAlreadyAdded) {
			aiState.bulkAIOrders.map((order) => {
				if (
					order.task.includes("translate") &&
					order.targetLang !== lang
				) {
					newTasks.push({
						...order,
						targetLang: lang,
					})
				} else {
					newTasks.push(order)
				}
			})
			setAIState({ bulkAIOrders: newTasks })
		}
	}

	const filterLanguageCodes = (languageCodes, searchInput) => {
		if (!searchInput) return languageCodes

		return languageCodes.filter((lang) => {
			return LANGUAGE_CODES[lang]
				.toLowerCase()
				.includes(searchInput.toLowerCase())
		})
	}

	useEffect(() => {
		if (state.storeLanguage || state.defaultTranslateLanguage) {
			setSelectedLanguage(
				state.defaultTranslateLanguage || state.storeLanguage,
			)
		}
	}, [state.storeLanguage, state.defaultTranslateLanguage])

	const successfulCheck = (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width="1rem"
			height="1rem"
			viewBox="0 0 24 24"
		>
			<path
				fill="currentColor"
				d="M9 16.17L5.53 12.7a.996.996 0 1 0-1.41 1.41l4.18 4.18c.39.39 1.02.39 1.41 0L20.29 7.71a.996.996 0 1 0-1.41-1.41L9 16.17z"
			></path>
		</svg>
	)

	const languageCodes = Object.keys(LANGUAGE_CODES)
	const filteredLanguageCodes = useMemo(
		() => filterLanguageCodes(languageCodes, languageSearchInput),
		[languageCodes, languageSearchInput],
	)

	const mantineCheckboxStyleReword = {
		label: {
			fontSize: "0.85rem",
			color: "#7415ac",
			paddingLeft: "0.5rem",
			cursor: "pointer",
		},
		input: {
			background: "#deadfa",
			borderColor: "#d381ff",
			"&:checked": {
				background: "#852eb7",
				borderColor: "transparent",
			},
			cursor: "pointer",
		},
	}

	const mantineCheckboxStyleTranslate = {
		label: {
			fontSize: "0.85rem",
			color: "#154cac",
			paddingLeft: "0.5rem",
			cursor: "pointer",
		},
		input: {
			background: "#9fcefd",
			borderColor: "#65b2ff",
			"&:checked": {
				background: "#164cac",
				borderColor: "transparent",
			},
			cursor: "pointer",
		},
	}

	return (
		<div style={{ display: "flex", alignItems: "start", gap: "1rem" }}>
			<div
				style={{
					width: "50%",
					background: "#e8c2ff",
					color: "#7415ac",
					border: "1px solid #dca3ff",
					borderRadius: "0.3rem",
					display: "flex",
					flexDirection: "column",
					cursor: "pointer",
				}}
			>
				<div
					style={{
						display: "flex",
						gap: "0.2rem",
						alignItems: "center",
						paddingTop: "0.1rem",
					}}
				>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							padding: "0.3rem 0.5rem",
							paddingRight: "0.4rem",
							borderRadius: "0.3rem 0rem 0rem 0.3rem",
						}}
					>
						<svg width="1rem" height="1rem" viewBox="0 0 15 15">
							<path
								fill="currentColor"
								fillRule="evenodd"
								d="M13.9.5a.4.4 0 0 0-.8 0v.6h-.6a.4.4 0 0 0 0 .8h.6v.6a.4.4 0 0 0 .8 0v-.6h.6a.4.4 0 0 0 0-.8h-.6V.5Zm-2.046 2.646a.5.5 0 0 1 0 .708l-1 1a.5.5 0 1 1-.707-.708l1-1a.5.5 0 0 1 .707 0Zm-2 2a.5.5 0 0 1 0 .708l-7 7a.5.5 0 1 1-.708-.708l7-7a.5.5 0 0 1 .708 0ZM13.5 5.1c.22 0 .4.18.4.4v.6h.6a.4.4 0 0 1 0 .8h-.6v.6a.4.4 0 0 1-.8 0v-.6h-.6a.4.4 0 0 1 0-.8h.6v-.6c0-.22.18-.4.4-.4ZM8.9.5a.4.4 0 0 0-.8 0v.6h-.6a.4.4 0 1 0 0 .8h.6v.6a.4.4 0 1 0 .8 0v-.6h.6a.4.4 0 0 0 0-.8h-.6V.5Z"
								clipRule="evenodd"
							></path>
						</svg>
					</div>
					<span style={{ fontSize: "0.85rem" }}>
						{t("ai.enhance_with")}{" "}
						<span style={{ fontWeight: "600" }}>{t("ai.ai")}</span>
					</span>
				</div>
				<div
					style={{
						padding: "0.5rem 0.5rem",
						borderTop: "1px solid #dca3ff",
						display: "flex",
						flexDirection: "column",
						gap: "0.2rem",
					}}
				>
					<Checkbox
						styles={mantineCheckboxStyleReword}
						label={t("ai.reword_titles")}
						onChange={(e) => onCheckboxChanged("reword", "title")}
					></Checkbox>
					<Checkbox
						styles={mantineCheckboxStyleReword}
						label={t("ai.reword_descriptions")}
						onChange={(e) =>
							onCheckboxChanged("reword", "description")
						}
					></Checkbox>
				</div>
			</div>

			<div
				style={{
					width: "50%",
					background: "#c2e1ff",
					color: "#154cac",
					border: "1px solid #88c3fd",
					borderRadius: "0.3rem",
					display: "flex",
					flexDirection: "column",
					cursor: "pointer",
				}}
			>
				<div
					style={{
						display: "flex",
						gap: "0.2rem",
						alignItems: "center",
						paddingTop: "0.1rem",
					}}
				>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							padding: "0.3rem 0.5rem",
							paddingRight: "0.4rem",
							borderRadius: "0.3rem 0rem 0rem 0.3rem",
						}}
					>
						<svg width="1rem" height="1rem" viewBox="0 0 32 32">
							<path
								fill="currentColor"
								d="M27.85 29H30l-6-15h-2.35l-6 15h2.15l1.6-4h6.85zm-7.65-6l2.62-6.56L25.45 23zM18 7V5h-7V2H9v3H2v2h10.74a14.71 14.71 0 0 1-3.19 6.18A13.5 13.5 0 0 1 7.26 9h-2.1a16.47 16.47 0 0 0 3 5.58A16.84 16.84 0 0 1 3 18l.75 1.86A18.47 18.47 0 0 0 9.53 16a16.92 16.92 0 0 0 5.76 3.84L16 18a14.48 14.48 0 0 1-5.12-3.37A17.64 17.64 0 0 0 14.8 7z"
							></path>
						</svg>
					</div>
					<span style={{ fontSize: "0.85rem" }}>
						{t("ai.translate_your_products")}
					</span>
				</div>

				<div
					style={{
						padding: "0.5rem 0.5rem",
						borderTop: "1px solid #88c3fd",
						display: "flex",
						flexDirection: "column",
						gap: "0.2rem",
					}}
				>
					<Checkbox
						styles={mantineCheckboxStyleTranslate}
						label={t("ai.translate_titles")}
						onChange={(e) =>
							onCheckboxChanged("translate", "title")
						}
					></Checkbox>
					<Checkbox
						styles={mantineCheckboxStyleTranslate}
						label={t("ai.translate_descriptions")}
						onChange={(e) =>
							onCheckboxChanged("translate", "description")
						}
					></Checkbox>
					<Checkbox
						styles={mantineCheckboxStyleTranslate}
						label={t("ai.translate_variants")}
						onChange={(e) =>
							onCheckboxChanged("translate", "variants")
						}
					></Checkbox>
				</div>

				<div
					style={{
						display: "flex",
						alignItems: "center",
						gap: "0.2rem",
						padding: "0.5rem 0.5rem",
						borderTop: "1px solid #88c3fd",
					}}
					onClick={(e) => toggleTranslatePopover(e)}
				>
					<span style={{ fontSize: "0.85rem" }}>
						{t("ai.translate_to")}{" "}
						<span
							style={{
								fontWeight: "600",
								textTransform: "capitalize",
								textDecoration: "underline",
							}}
						>
							{getLanguageName(selectedLanguage, i18n.language)}
						</span>
					</span>
					<div
						className="language-selector-icon"
						style={{ height: "0.95rem", width: "0.95rem" }}
					>
						<svg
							style={{ display: "block" }}
							width="0.95rem"
							height="0.95rem"
							viewBox="0 0 512 512"
						>
							<path
								d="M128 192l128 128 128-128z"
								fill="currentColor"
							></path>
						</svg>
					</div>
				</div>

				<Popover
					active={translatePopoverActive}
					activator={<div></div>}
					onClose={() => setTranslatePopoverActive(false)}
				>
					<div
						style={{
							padding: "0.5rem",
							height: "12rem",
							width: "9rem",
							overflowY: "scroll",
						}}
					>
						<p
							style={{
								fontSize: "0.7rem",
								marginBottom: "0.5rem",
							}}
						>
							{t("ai.your_store_languages")}
						</p>

						<div
							style={{
								display: "flex",
								flexDirection: "column",
								gap: "0.1rem",
							}}
						>
							{state.availableStoreLanguages.map((lang) => {
								return (
									<div
										key={lang}
										onClick={() =>
											changeSelectedLanguage(lang)
										}
										className="language-button"
									>
										<svg
											xmlns="http://www.w3.org/2000/svg"
											width="0.85rem"
											height="0.85rem"
											viewBox="0 0 32 32"
										>
											<path
												fill="currentColor"
												d="M27.85 29H30l-6-15h-2.35l-6 15h2.15l1.6-4h6.85zm-7.65-6l2.62-6.56L25.45 23zM18 7V5h-7V2H9v3H2v2h10.74a14.71 14.71 0 0 1-3.19 6.18A13.5 13.5 0 0 1 7.26 9h-2.1a16.47 16.47 0 0 0 3 5.58A16.84 16.84 0 0 1 3 18l.75 1.86A18.47 18.47 0 0 0 9.53 16a16.92 16.92 0 0 0 5.76 3.84L16 18a14.48 14.48 0 0 1-5.12-3.37A17.64 17.64 0 0 0 14.8 7z"
											></path>
										</svg>
										<span
											style={{
												fontSize: "0.85rem",
												textTransform: "capitalize",
											}}
										>
											{getLanguageName(
												lang,
												i18n.language,
											)}
										</span>
									</div>
								)
							})}
						</div>

						<div
							style={{
								borderTop: "1px solid #ccc",
								paddingTop: "1rem",
								marginTop: "0.5rem",
							}}
						>
							<p
								style={{
									fontSize: "0.7rem",
									marginBottom: "0.5rem",
								}}
							>
								{t("ai.all_languages")}
							</p>

							<Input
								variant="filled"
								size="xs"
								radius="md"
								placeholder={t("ai.search_language")}
								value={languageSearchInput}
								onChange={(e) =>
									setLanguageSearchInput(e.target.value)
								}
							/>

							<div
								style={{
									display: "flex",
									flexDirection: "column",
									gap: "0.1rem",
									marginTop: "0.5rem",
								}}
							>
								{filteredLanguageCodes.map((lang) => {
									return (
										<div
											key={lang}
											onClick={() =>
												changeSelectedLanguage(lang)
											}
											className="language-button"
										>
											<svg
												xmlns="http://www.w3.org/2000/svg"
												width="0.85rem"
												height="0.85rem"
												viewBox="0 0 32 32"
											>
												<path
													fill="currentColor"
													d="M27.85 29H30l-6-15h-2.35l-6 15h2.15l1.6-4h6.85zm-7.65-6l2.62-6.56L25.45 23zM18 7V5h-7V2H9v3H2v2h10.74a14.71 14.71 0 0 1-3.19 6.18A13.5 13.5 0 0 1 7.26 9h-2.1a16.47 16.47 0 0 0 3 5.58A16.84 16.84 0 0 1 3 18l.75 1.86A18.47 18.47 0 0 0 9.53 16a16.92 16.92 0 0 0 5.76 3.84L16 18a14.48 14.48 0 0 1-5.12-3.37A17.64 17.64 0 0 0 14.8 7z"
												></path>
											</svg>
											<span
												style={{
													fontSize: "0.85rem",
													textTransform: "capitalize",
												}}
											>
												{getLanguageName(
													lang,
													i18n.language,
												)}
											</span>
										</div>
									)
								})}
							</div>
						</div>
					</div>
				</Popover>
			</div>

			<style>{`
				.ai-orders-buttons-strip {
					filter: opacity(0.6) blur(1.5px);
					transition: filter 0.2s ease-in-out;
				}
				.ai-orders-buttons-strip:hover {
					filter: opacity(0.5) blur(0px);
				}

				.language-button {
					display: flex;
					gap: 0.3rem;
					align-items: center;
					padding: 0.2rem 0.3rem;
					cursor: pointer;
					background: none;
					border-radius: 0.3rem;
				}
				.language-button:hover {
					background: #e8e8e8;
				}
				.language-selector-icon:hover {
					border-radius: 0.25rem;
					background: #92bde7;
				}
			`}</style>
		</div>
	)
}
